/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module MessageItemsModule
 */

import {
    HTTPProtocol,
    IHTTPRedirectAction,
    IURIParam,
    IURIParamToken,
    URIParamType,
} from 'generated-types';

import {
    isEmpty,
    reduce,
} from 'underscore';

import { DefaultValues } from 'ajs/modules/core/services/default-values.service';
import { HTTPRedirectAction } from 'object-types';
import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';
import { RepeatedMessageItem } from 'ajs/modules/data-model/factories/repeated-message-item.factory';
import { SchemaService } from 'ajs/modules/core/services';

type THTTPRedirectActionPartial = Omit<IHTTPRedirectAction, 'host' | 'path'>;

type TURIParamPartial = Omit<IURIParam, 'tokens'>;

/**
 * Interface to update tokens as repeatedMessageItem.
 */
interface IURIParamConfig extends TURIParamPartial {
    tokens: RepeatedMessageItem<MessageItem<IURIParamToken>>,
}

/**
 * Extended IHTTPRedirectAction Interface to add UI only fields.
 */
interface IHTTPRedirectActionConfig extends THTTPRedirectActionPartial {
    host?: MessageItem<IURIParamConfig>,
    path?: MessageItem<IURIParamConfig>,
    hostStr?: string,
    pathStr?: string,
}

/**
 * Const for Existing Host String.
 */
const EXISTING_HOST = '<ExistingHost>';

/**
 * Const for Existing Path String.
 */
const EXISTING_PATH = '<ExistingPath>';

/**
 * Const for Keep Query String.
 */
const KEEP_QUERY_STRING = '<KeepQueryString>';

/**
 * Const for Remove Query String.
 */
const REMOVE_QUERY_STRING = '<RemoveQueryString>';

/**
 * @description HTTPRedirectAction ConfigItem.
 *
 * @author Nisar Nadaf, Umesh Chovatiya
 */
export class HTTPRedirectActionConfigItem extends MessageItem<IHTTPRedirectActionConfig> {
    /**
     * Dependencies that are required.
     */
    public static ajsDependencies = [
        'RangeParser',
        'schemaService',
    ];

    /**
     * Schema Service Instance
     */
    private schemaService: SchemaService;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: HTTPRedirectAction,
            ...args,
        };

        super(extendedArgs);
        this.schemaService = this.getAjsDependency_('schemaService');
    }

    /**
     * Getter for protocol string from HTTPProtocol Enum.
     */
    public get httpProtocol(): string {
        return this.schemaService.getEnumValueLabel('HTTPProtocol', this.config.protocol);
    }

    /**
     * Getter for Redirect Status Code string from HTTPRedirectStatusCode Enum.
     */
    public get redirectStatusCode(): string | null {
        return this.config.status_code ?
            this.schemaService.getEnumValueLabel('HTTPRedirectStatusCode',
                this.config.status_code) : null;
    }

    /**
     * @override
     */
    // eslint-disable-next-line no-underscore-dangle
    public get defaultConfigOverride_(): Partial<IHTTPRedirectActionConfig> {
        const defaultValues: DefaultValues = this.getAjsDependency_('defaultValues');
        const type = this.objectType.toLowerCase();
        const defaultConfig = defaultValues.getDefaultItemConfigByType(type) || {};

        return {
            ...defaultConfig,
            protocol: HTTPProtocol.HTTP,
            port: 80,
        };
    }

    /**
     * Extracts value from the config and prepares a string
     * to show action details in the grid expander.
     */
    public actionReadonlyDetails(): string {
        const {
            hostStr,
            pathStr,
            port,
            keep_query: keepQuery,
        } = this.config;

        const { httpProtocol } = this;

        const hostStrDisplayValue = hostStr ?? EXISTING_HOST;
        const pathStrDisplayValue = pathStr ?? EXISTING_PATH;
        const queryDisplayValue = keepQuery ? KEEP_QUERY_STRING : REMOVE_QUERY_STRING;

        return `${httpProtocol}://${hostStrDisplayValue}:${port}/${pathStrDisplayValue}?${queryDisplayValue}`;
    }

    /**
     * @override
     * Convert host and path tokens to string values required on UI.
     */
    public modifyConfigDataAfterLoad(): void {
        const { host, path } = this.config;

        super.modifyConfigDataAfterLoad();

        if (!host) {
            this.safeSetNewChildByField('host');
        } else if (host && host.config.tokens) {
            this.config.hostStr = this.extractStringValue(host.config.tokens, '.');
        }

        if (!path) {
            this.safeSetNewChildByField('path');
        } else if (path && path.config.tokens) {
            const value = this.extractStringValue(path.config.tokens, '/');

            this.config.pathStr = !isEmpty(value) && value || '/';
        }
    }

    /**
     * @override
     * Convert host and path string values to tokenised params.
     */
    public modifyConfigDataBeforeSave(): void {
        const { hostStr, pathStr } = this.config;

        const rangeParser = this.getAjsDependency_('RangeParser');

        super.modifyConfigDataBeforeSave();

        if (hostStr) {
            this.config.host.updateConfig({
                type: URIParamType.URI_PARAM_TYPE_TOKENIZED,
                tokens: rangeParser.tokensStr2tokens(hostStr, '.'),
            });

            delete this.config.hostStr;
        } else {
            delete this.config.host;
        }

        if (pathStr) {
            this.config.path.updateConfig({
                type: URIParamType.URI_PARAM_TYPE_TOKENIZED,
                tokens: rangeParser.tokensStr2tokens(pathStr, '/'),
            });

            delete this.config.pathStr;
        } else {
            delete this.config.path;
        }
    }

    /**
     * Handler for protocol change. Update ports as per the protocol selection.
     */
    public handleProtocolChange(): void {
        const { protocol } = this.config;

        if (protocol === HTTPProtocol.HTTP) {
            this.config.port = 80;
        }

        if (protocol === HTTPProtocol.HTTPS) {
            this.config.port = 443;
        }
    }

    /**
     * Convert host and path tokens to formatted string value.
     */
    private extractStringValue(
        tokens: RepeatedMessageItem<MessageItem<IURIParamToken>>,
        separator: string,
    ): string {
        const rangeParser = this.getAjsDependency_('RangeParser');

        return reduce(tokens.config,
            function(base, token) {
                return `${base + rangeParser.token2str(token.config)}${separator}`;
            },
            '').slice(0, -1);
    }
}
